import type {ReactNode} from 'react';
import {createContext, useCallback, useContext, useEffect, useMemo, useState} from 'react';
import useNetworkStatus from 'src/hooks/use-network-status';
import type { UseNetworkStatusReturn } from 'src/hooks/use-network-status';
import {useWsContext} from 'src/websocket';
import { createTransaction } from 'src/api/server/cashier';
import { useDispatch } from 'src/redux/store';
import { updateInvoices, removeDraft, removeSync } from 'src/redux/slice/invoice';
import SyncManager from './sync-manager';
import type {QueueItem, QueueItemInput, QueueStats, SyncOptions} from './types';

/**
 * Offline Context
 * Provides offline state and sync functionality to the app
 */

interface OfflineContextValue {
  // Network state
  isOnline: boolean;
  isOffline: boolean;
  wasOffline: boolean;
  wsConnected: boolean;
  network: UseNetworkStatusReturn;

  // Queue state
  queueStats: QueueStats;
  pendingItems: QueueItem[];
  hasPendingItems: boolean;

  // Actions
  enqueue: (item: QueueItemInput) => Promise<QueueItem>;
  sync: (options?: SyncOptions) => Promise<any[]>;
  retryItem: (id: string) => Promise<void>;
  removeItem: (id: string) => Promise<void>;
  clearCompleted: () => Promise<number>;

  // Status
  isSyncing: boolean;
  lastSyncAt: Date | null;

  // Configuration
  autoSync: boolean;
  setAutoSync: (value: boolean) => void;

  // Sync manager for advanced usage
  syncManager: SyncManager;
}

const OfflineContext = createContext<OfflineContextValue | null>(null);

interface OfflineProviderProps {
  children: ReactNode;
  autoSync?: boolean;
  syncOnReconnect?: boolean;
}

function OfflineProvider({
  children,
  autoSync: autoSyncProp = true,
  syncOnReconnect = true,
}: OfflineProviderProps) {
  const syncManager = useMemo(() => SyncManager.getInstance(), []);
  const dispatch = useDispatch();
  const { wsConnected } = useWsContext();
  const network = useNetworkStatus();
  const { isOnline, isOffline, wasOffline } = network;

  // Local state
  const [queueStats, setQueueStats] = useState<QueueStats>({
    total: 0,
    pending: 0,
    processing: 0,
    completed: 0,
    failed: 0,
    retrying: 0,
  });
  const [pendingItems, setPendingItems] = useState<QueueItem[]>([]);
  const [isSyncing, setIsSyncing] = useState(false);
  const [lastSyncAt, setLastSyncAt] = useState<Date | null>(null);
  const [autoSync, setAutoSyncState] = useState(autoSyncProp);

  // Update queue stats
  const refreshQueueStats = useCallback(async () => {
    const stats = await syncManager.getStats();
    setQueueStats(stats);

    const items = await syncManager.getPendingItems();
    setPendingItems(items);
  }, [syncManager]);

  // Transaction sync must be registered at application level. If it lives in
  // the checkout page, navigating to Invoices unregisters it exactly when a
  // cashier needs to recover an uncertain sale after a network reset.
  useEffect(() => {
    const handler = async (item: QueueItem) => {
      const queued = item?.data as any;
      const userId = queued?.userId;
      const configId = queued?.configId;
      const transactionPayload = queued?.data;

      if (!userId || !configId || !transactionPayload) {
        throw new Error('Invalid queued transaction payload');
      }

      const response: any = await createTransaction(userId, configId, transactionPayload);
      const transaction = response?.transaction || response?.data;

      if (response?.success === false || response?.error || !transaction) {
        throw new Error(response?.error || 'Transaction synchronization failed');
      }

      // As soon as MongoDB confirms the transaction, replace the local draft.
      // ERP synchronization can then retry independently without risking loss
      // of the sale or replaying local stock/payment side effects.
      dispatch(updateInvoices({ data: [transaction] }));

      const identifiers = [
        transaction?.idempotencyKey,
        transaction?.transactionNumber,
        queued?.localInvoiceId,
      ].filter(Boolean);

      for (const identifier of identifiers) {
        dispatch(removeDraft(identifier));
      }

      if (transaction?.syncStatus === 'failed') {
        throw new Error(
          transaction?.syncError || response?.warning || 'ERP synchronization failed'
        );
      }

      // A reset connection can be retried while the original ERP call is still
      // running. Keep the queue item retryable until the backend reports a
      // terminal state; otherwise an eventual ERP failure could be missed when
      // the WebSocket is also disconnected.
      if (transaction?.syncStatus === 'in_progress' || transaction?.syncStatus === 'pending') {
        throw new Error('Transaction synchronization is still in progress');
      }

      for (const identifier of identifiers) {
        dispatch(removeSync(identifier));
      }
    };

    syncManager.registerSyncHandler('transaction_create', handler);
    return () => syncManager.unregisterSyncHandler('transaction_create');
  }, [dispatch, syncManager]);

  // Initial load and periodic refresh
  useEffect(() => {
    refreshQueueStats().then();

    // Subscribe to queue changes
    const unsubscribe = syncManager.subscribe(() => {
      refreshQueueStats().then();
    });

    // Periodic refresh every 5 seconds
    const interval = setInterval(refreshQueueStats, 5000);

    return () => {
      unsubscribe();
      clearInterval(interval);
    };
  }, [syncManager, refreshQueueStats]);

  // Auto-sync when coming back online
  useEffect(() => {
    if (wasOffline && isOnline && autoSync && syncOnReconnect) {
      console.info('[OfflineProvider] Network restored, triggering auto-sync');
      syncManager.sync({
        retryFailed: true,
        onComplete: (results) => {
          console.info('[OfflineProvider] Auto-sync complete:', results);
          setLastSyncAt(new Date());
        },
      }).then();
    }
  }, [wasOffline, isOnline, autoSync, syncOnReconnect, syncManager]);

  // Auto-sync whenever the API is reachable - WebSocket state is not a transport prerequisite
  useEffect(() => {
    if (isOnline && autoSync) {
      // Check directly with syncManager instead of relying on potentially stale queueStats
      syncManager.getStats().then((stats) => {
        const hasPending = stats.pending > 0 || stats.failed > 0;
        if (hasPending) {
          console.info('[OfflineProvider] API reachable with pending items, triggering sync', stats);
          syncManager.sync({
            retryFailed: true,
            onComplete: (results) => {
              console.info('[OfflineProvider] API reconnect sync complete:', results);
              setLastSyncAt(new Date());
              refreshQueueStats().then();
            },
          }).then();
        }
      });
    }
  }, [isOnline, autoSync, syncManager, refreshQueueStats]);

  // Periodic sync check while the API is reachable (every 15 seconds)
  useEffect(() => {
    if (!isOnline || !autoSync) return;

    const interval = setInterval(() => {
      syncManager.getStats().then((stats) => {
        if (stats.total !== (stats.completed + stats.processing)) {
          console.info('[OfflineProvider] Periodic check found pending items, triggering sync', stats);
          syncManager.sync({
            retryFailed: true,
            onComplete: (results) => {
              console.info('[OfflineProvider] Periodic sync complete:', results);
              setLastSyncAt(new Date());
              refreshQueueStats().then();
            },
          }).then();
        }
      });
    }, 15000); // Check every 15 seconds

    return () => clearInterval(interval);
  }, [isOnline, autoSync, syncManager, refreshQueueStats]);

  // Actions
  const sync = useCallback(
    async (options: SyncOptions = {}) => {
      setIsSyncing(true);
      try {
        return await syncManager.sync({
          ...options,
          onComplete: (results) => {
            setLastSyncAt(new Date());
            options.onComplete?.(results);
            refreshQueueStats();
          },
        });
      } finally {
        setIsSyncing(false);
      }
    },
    [syncManager, refreshQueueStats]
  );

  const enqueue = useCallback(
    async (item: QueueItemInput) => {
      const queueItem = await syncManager.enqueue(item);
      await refreshQueueStats();
      setAutoSyncState(true)

      // For transaction_create, a transport error can mean the first POST is
      // still running server-side (ERR_CONNECTION_RESET is an unknown outcome).
      // Avoid an immediate concurrent replay: the 15 s periodic sync gives the
      // server time to persist the transaction, after which idempotency safely
      // returns/retries the canonical Mongo record. Other queue types can sync
      // immediately as before.
      if (isOnline && autoSync && item.type !== 'transaction_create') {
        sync().then(() => {});
      }

      return queueItem;
    },
    [syncManager, isOnline, autoSync, sync, refreshQueueStats]
  );

  const retryItem = useCallback(
    async (id: string) => {
      await syncManager.retryItem(id);
      await refreshQueueStats();

      // Trigger sync if online
      if (isOnline) {
        sync().then();
      }
    },
    [syncManager, isOnline, sync, refreshQueueStats]
  );

  const removeItem = useCallback(
    async (id: string) => {
      await syncManager.removeItem(id);
      await refreshQueueStats();
    },
    [syncManager, refreshQueueStats]
  );

  const clearCompleted = useCallback(async () => {
    const count = await syncManager.clearCompleted();
    await refreshQueueStats();
    return count;
  }, [syncManager, refreshQueueStats]);

  const setAutoSync = useCallback((value: boolean) => {
    setAutoSyncState(value);
    syncManager.updateConfig({ autoSync: value });
  }, [syncManager]);

  const value = useMemo(
    () => ({
      isOnline,
      isOffline,
      wsConnected,
      network,
      wasOffline,
      queueStats,
      pendingItems,
      hasPendingItems: queueStats.pending > 0 || queueStats.failed > 0 || queueStats.retrying > 0,
      enqueue,
      sync,
      retryItem,
      removeItem,
      clearCompleted,
      isSyncing,
      lastSyncAt,
      autoSync,
      setAutoSync,
      syncManager,
    }),
    [
      isOnline,
      isOffline,
      wsConnected,
      network,
      wasOffline,
      queueStats,
      pendingItems,
      enqueue,
      sync,
      retryItem,
      removeItem,
      clearCompleted,
      isSyncing,
      lastSyncAt,
      autoSync,
      setAutoSync,
      syncManager,
    ]
  );

  return <OfflineContext.Provider value={value}>{children}</OfflineContext.Provider>;
}

/**
 * Hook to access offline context
 */
export function useOfflineContext(): OfflineContextValue {
  const context = useContext(OfflineContext);
  if (!context) {
    throw new Error('useOfflineContext must be used within an OfflineProvider');
  }
  return context;
}

export { OfflineContext, OfflineProvider };
